import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePostcodeSearch } from './usePostcodeSearch';
import schoolLogo from '../school-brand.jpg';
import * as Styles from './styles';

function RegisterMobile() {
    const navigate = useNavigate();
    const loginTopRef = useRef(null);

    // Quick Login Drawer State
    const [showQuickLogin, setShowQuickLogin] = useState(false);
    const [quickEmail, setQuickEmail] = useState('');
    const [quickPassword, setQuickPassword] = useState('');

    // Forgot Password States
    const [isForgotMode, setIsForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotMessage, setForgotMessage] = useState('');

    const handleQuickLoginClick = () => {
        setShowQuickLogin(true);
        setIsForgotMode(false);
        setForgotMessage('');
        if (loginTopRef.current) {
            loginTopRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleQuickLogin = async (e) => {
        e.preventDefault();
        try {
            localStorage.removeItem('instructorId');
            localStorage.removeItem('learnerId');
            localStorage.removeItem('userId');
            localStorage.removeItem('adminToken');

            const response = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: quickEmail, password: quickPassword })
            });
            const data = await response.json();
            if (response.ok) {
                localStorage.setItem('needsPasswordReset', data.needsPasswordReset ? 'true' : 'false');

                if (data.role === 'admin') {
                    localStorage.setItem('userRole', 'admin');
                    localStorage.setItem('adminToken', data.token || 'mock-admin-token-secure');
                    window.location.assign('/admin');
                } else if (data.role === 'instructor') {
                    localStorage.setItem('userRole', 'instructor');
                    localStorage.setItem('instructorId', data.id || data.instructorId);
                    localStorage.setItem('token', data.token || '');
                    window.location.assign('/instructor-dash');
                } else {
                    localStorage.setItem('userRole', 'learner');
                    localStorage.setItem('userId', data.id || data.userId);
                    localStorage.setItem('learnerId', data.id || data.userId);
                    localStorage.setItem('userName', data.name || "Learner Driver");
                    localStorage.setItem('token', data.token || '');
                    window.location.assign(`/learner-dash/${data.instructor_id || 5}`);
                }
            } else {
                alert(data.error || "Login failed.");
            }
        } catch (err) {
            alert("Server error.");
        }
    };

    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch('/api/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail })
            });
            const data = await res.json();
            if (res.ok) {
                setForgotMessage('Password reset link sent! Check your email.');
            } else {
                setForgotMessage(data.error || 'Could not send reset link.');
            }
        } catch (err) {
            setForgotMessage('Server error. Please try again.');
        }
    };

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [activeStep, setActiveStep] = useState('step1');

    const [title, setTitle] = useState('Mr');
    const [firstName, setFirstName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [postcode, setPostcode] = useState('');

    const [travelRadius, setTravelRadius] = useState(5);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const {
        nearbyAreas,
        findNearbyAreas,
        loading,
        isValid
    } = usePostcodeSearch();

    const availableCategories = [
        "Car (Manual)",
        "Car (Automatic)",
        "Motorbike License"
    ];

    useEffect(() => {
        if (postcode && postcode.replace(/\s/g, '').length >= 3) {
            const timer = setTimeout(() => {
                findNearbyAreas(postcode, travelRadius);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [postcode, travelRadius, findNearbyAreas]);

    const handleCategoryToggle = (category) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(item => item !== category)
                : [...prev, category]
        );
    };

    const handleAreaToggle = (area) => {
        setSelectedAreas(prev =>
            prev.includes(area)
                ? prev.filter(item => item !== area)
                : [...prev, area]
        );
    };

    const handleApply = async (e) => {
        e.preventDefault();
        if (!firstName || !surname || !email || !phone) {
            alert("Please fill in your name, email, and telephone number.");
            setActiveStep('step1');
            return;
        }
        if (!agreedToTerms) {
            alert("You must agree to the Terms & Conditions before registering.");
            return;
        }

        try {
            const payload = new FormData();
            payload.append('name', `${title} ${firstName} ${surname}`);
            payload.append('email', email);
            payload.append('phone', phone);
            payload.append('address', address || '');
            payload.append('postcode', postcode || '');
            payload.append('travel_radius_miles', travelRadius);
            payload.append('transmission_type', JSON.stringify(selectedCategories.length > 0 ? selectedCategories : ["Car (Manual)"]));
            payload.append('selected_areas', JSON.stringify(selectedAreas));

            const response = await fetch('/api/instructors', {
                method: 'POST',
                body: payload
            });

            if (response.ok) {
                setIsSubmitted(true);
            } else {
                const data = await response.json().catch(() => ({}));
                alert(`❌ Failed: ${data.error || "Unknown error."}`);
            }
        } catch (err) {
            alert("❌ Network error. Please try again.");
        }
    };

    if (isSubmitted) {
        return (
            <div style={{ minHeight: '100vh', background: '#0f172a', color: '#ffffff', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                <div style={{ background: '#162238', padding: '30px 20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center', width: '100%', maxWidth: '400px' }}>
                    <div style={{ width: '50px', height: '50px', margin: '0 auto 15px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>✓</div>
                    <h2 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '8px', color: '#ffffff' }}>Registration Received</h2>
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '20px' }}>
                        Thank you for registering with LessonLane. We will review your application and be in touch soon.
                    </p>
                    <button onClick={() => navigate('/')} style={{ padding: '10px 20px', backgroundColor: '#3b82f6', color: '#ffffff', borderRadius: '8px', fontWeight: '700', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#0f172a', color: '#ffffff', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            
            <div ref={loginTopRef} />

            {/* UNIFORM APP HEADER */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#090d16', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 1000 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={schoolLogo} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #3b82f6' }} />
                    <Link to="/" style={{ color: '#fff', fontWeight: '800', fontSize: '1.1rem', letterSpacing: '-0.5px', textDecoration: 'none' }}>LessonLane</Link>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Link to="/" title="Home" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '6px 10px', borderRadius: '8px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="#FFFFFF"/>
                        </svg>
                    </Link>
                    <button onClick={handleQuickLoginClick} title="Portal Login" style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C9.24 2 7 4.24 7 7C7 9.42 8.74 11.43 11 11.9V13.1C7.58 13.54 5 16.48 5 20H19C19 16.48 16.42 13.54 13 13.1V11.9C15.26 11.43 17 9.42 17 7C17 4.24 14.76 2 12 2Z" fill="#FFFFFF"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* QUICK LOGIN BOX / FORGOT PASSWORD */}
            {showQuickLogin && (
                <div style={{ padding: '16px 16px 0 16px', maxWidth: '500px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                    {!isForgotMode ? (
                        <form onSubmit={handleQuickLogin} style={{ background: '#162238', padding: '18px', borderRadius: '14px', border: '1.5px solid #3b82f6', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#ffffff', fontWeight: '800' }}>Portal Sign In</h4>
                                <button type="button" onClick={() => setShowQuickLogin(false)} style={{ background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                            </div>
                            <input type="email" placeholder="Email address" value={quickEmail} onChange={e => setQuickEmail(e.target.value)} required style={{ width: '100%', padding: '10px 12px', marginBottom: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }} />
                            <input type="password" placeholder="Password" value={quickPassword} onChange={e => setQuickPassword(e.target.value)} required style={{ width: '100%', padding: '10px 12px', marginBottom: '6px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }} />
                            <div style={{ textAlign: 'right', marginBottom: '10px' }}>
                                <button 
                                    type="button"
                                    onClick={() => { setIsForgotMode(true); setForgotMessage(''); }}
                                    style={{ background: 'none', border: 'none', fontSize: '11.5px', color: '#60a5fa', textDecoration: 'none', cursor: 'pointer', padding: 0 }}
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <button type="submit" style={{ width: '100%', padding: '10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>Sign In ➔</button>
                        </form>
                    ) : (
                        <form onSubmit={handleForgotPasswordSubmit} style={{ background: '#162238', padding: '18px', borderRadius: '14px', border: '1.5px solid #3b82f6', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#ffffff', fontWeight: '800' }}>Reset Password</h4>
                                <button type="button" onClick={() => setShowQuickLogin(false)} style={{ background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                            </div>
                            <p style={{ fontSize: '11.5px', color: '#94a3b8', marginBottom: '10px', lineHeight: '1.4' }}>
                                Enter your account email and we'll send you a recovery link.
                            </p>
                            <input type="email" placeholder="Enter your email address" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required style={{ width: '100%', padding: '10px 12px', marginBottom: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', boxSizing: 'border-box', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }} />
                            {forgotMessage && (
                                <div style={{ fontSize: '11.5px', color: forgotMessage.includes('sent') ? '#10b981' : '#f87171', marginBottom: '8px' }}>
                                    {forgotMessage}
                                </div>
                            )}
                            <button type="submit" style={{ width: '100%', padding: '10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>Send Reset Link ➔</button>
                            <div style={{ textAlign: 'center' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setIsForgotMode(false)}
                                    style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                    Back to Sign In
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            <div style={{ padding: '24px 16px 40px', maxWidth: '600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                
                {/* HEADER */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ color: '#60a5fa', fontSize: '0.72rem', fontWeight: '800', letterSpacing: '1px', marginBottom: '6px' }}>INSTRUCTOR REGISTRATION</div>
                    <h1 style={{ fontSize: '1.6rem', fontWeight: '900', margin: '0 0 6px', color: '#ffffff' }}>Create Account</h1>
                    <p style={{ color: '#94a3b8', fontSize: '0.84rem', lineHeight: '1.4' }}>Join LessonLane and manage your students, schedule, and progress.</p>
                </div>

                <form onSubmit={handleApply} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    
                    {/* STEP 1 */}
                    <div style={{ background: '#162238', padding: '18px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#fff', margin: '0 0 12px' }}>1. Personal Details</h3>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <select value={title} onChange={e => setTitle(e.target.value)} style={{ ...inputStyle, width: '80px' }}>
                                <option>Mr</option><option>Mrs</option><option>Ms</option><option>Dr</option>
                            </select>
                            <input type="text" placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} required style={inputStyle} />
                        </div>
                        <input type="text" placeholder="Surname" value={surname} onChange={e => setSurname(e.target.value)} required style={{ ...inputStyle, marginBottom: '10px' }} />
                        <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} required style={{ ...inputStyle, marginBottom: '10px' }} />
                        <input type="tel" placeholder="Phone number" value={phone} onChange={e => setPhone(e.target.value)} required style={inputStyle} />
                    </div>

                    {/* STEP 2 */}
                    <div style={{ background: '#162238', padding: '18px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#fff', margin: '0 0 12px' }}>2. Teaching Area</h3>
                        <input type="text" placeholder="Home address" value={address} onChange={e => setAddress(e.target.value)} style={{ ...inputStyle, marginBottom: '10px' }} />
                        <input type="text" placeholder="Postcode (e.g. NG18 1AA)" value={postcode} onChange={e => { setPostcode(e.target.value); if (e.target.value.length > 2) findNearbyAreas(e.target.value, travelRadius); }} style={{ ...inputStyle, textTransform: 'uppercase', marginBottom: '12px' }} />
                        
                        <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '6px' }}>Travel Radius: {travelRadius} miles</label>
                        <input type="range" min="1" max="10" value={travelRadius} onChange={e => { setTravelRadius(e.target.value); if (postcode.length > 2) findNearbyAreas(postcode, e.target.value); }} style={{ width: '100%', accentColor: '#3b82f6', marginBottom: '12px' }} />

                        {nearbyAreas.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {nearbyAreas.map(area => (
                                    <button type="button" key={area} onClick={() => handleAreaToggle(area)} style={{ padding: '6px 10px', background: selectedAreas.includes(area) ? '#3b82f6' : '#0f172a', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                                        {selectedAreas.includes(area) ? '✓ ' : ''}{area}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* STEP 3 */}
                    <div style={{ background: '#162238', padding: '18px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#fff', margin: '0 0 12px' }}>3. Vehicle Types</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                            {availableCategories.map(cat => (
                                <button type="button" key={cat} onClick={() => handleCategoryToggle(cat)} style={{ padding: '8px 12px', background: selectedCategories.includes(cat) ? '#3b82f6' : '#0f172a', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                                    {selectedCategories.includes(cat) ? '✓ ' : ''}{cat}
                                </button>
                            ))}
                        </div>

                        {/* TERMS */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#0f172a', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <input type="checkbox" id="terms" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} required style={{ marginTop: '2px', cursor: 'pointer' }} />
                            <label htmlFor="terms" style={{ fontSize: '12px', color: '#94a3b8', lineHeight: '1.4', cursor: 'pointer' }}>
                                I agree to the <Link to="/terms" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })} style={{ color: '#60a5fa', textDecoration: 'underline' }}>Terms & Conditions</Link>.
                            </label>
                        </div>
                    </div>

                    <button type="submit" style={{ width: '100%', padding: '13px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '800', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>
                        Create Account
                    </button>
                </form>

            </div>

        </div>
    );
}

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#ffffff',
    borderRadius: '8px',
    fontSize: '13px',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit'
};

export default RegisterMobile;